import { IFieldChange, IProductHistoryEvent } from "@/common/types";
import { ChangesWrapper, TimelineItem } from "../style";
import {
  eventColorKey,
  formatChangeValue,
  formatEventDate,
  formatShortDate,
  sameChanges,
} from "../format";

export interface IHistoryEventProps {
  event: IProductHistoryEvent;
}

interface IChangeListProps {
  title: string;
  subtitle?: string;
  changes: IFieldChange[];
  highlight?: boolean;
}

function ChangeList({ title, subtitle, changes, highlight }: IChangeListProps) {
  return (
    <ChangesWrapper highlight={highlight}>
      <span className="changes-title">{title}</span>
      {subtitle ? <span className="changes-subtitle">{subtitle}</span> : null}
      {changes.map((change) => (
        <div className="change-row" key={change.field}>
          <span className="change-field">{change.label}</span>
          <span className="change-from">{formatChangeValue(change.from)}</span>
          <span className="change-arrow">&rarr;</span>
          <span className="change-to">{formatChangeValue(change.to)}</span>
        </div>
      ))}
    </ChangesWrapper>
  );
}

/** One immutable lifecycle event in the approval-history timeline. */
export function HistoryEvent({ event }: IHistoryEventProps) {
  const { actor } = event;
  const actorRole = actor?.role;
  const changedFields = event.changed_fields ?? [];

  // MED-678: the backend supplies the wording for an event whose acting user was
  // never recorded ("Not recorded"). It must not be re-invented here — the old
  // `|| "System"` fallback attributed real people's work to a machine.
  const actorName = actor?.display_name ?? "";
  const unattributed = actor ? !actor.attributed : true;

  // A submission is answered with the company: Medex approved the company, and
  // it is the company a query or a decline is served on. The person who acted
  // for it is the audit detail underneath, in smaller type.
  //
  // `company_name` is the test rather than the event type, so the client keeps
  // no second copy of which types are owner-side. It also degrades on its own:
  // a decision has no company and renders as the person alone, and a row
  // reconstructed by the MED-653 backfill has a company but never captured a
  // person, so the second line simply does not appear.
  const actorCompany = actor?.company_name;
  const actorPerson = actorCompany ? actor?.name : null;
  const actorPrimary = actorCompany || actorName;
  // The role belongs to the person, so it sits with whichever line names them.
  const roleSuffix = actorRole ? ` (${actorRole})` : "";

  // MED-678: what the admin is actually approving — the difference from the
  // version they last approved, not the difference from whatever the product
  // looked like a moment before the latest keystroke.
  const comparison = event.changes_since_last_approval;
  const cumulative = comparison?.changes ?? [];
  const hasComparison = cumulative.length > 0;
  // One edit since the approval means the two lists are identical; showing the
  // same rows twice under two headings is noise, so the single edit is folded
  // into the comparison block rather than repeated beneath it.
  const editIsTheWholeStory = hasComparison && sameChanges(cumulative, changedFields);

  const comparisonSubtitle =
    comparison && comparison.baseline === "last_approved"
      ? `Last approved ${
          formatShortDate(comparison.baseline_at) || "previously"
        }${editIsTheWholeStory ? "" : " · composed from every edit since"}`
      : "This product has never been approved — compared with its first submission";

  return (
    <TimelineItem colorKey={eventColorKey(event.event_type)}>
      <div className="track">
        <div className="dot" />
        <div className="line" />
      </div>

      <div className="event">
        <div className="event-head">
          <h4 className="event-label">{event.label}</h4>
          <span className="event-date">{formatEventDate(event.occurred_at)}</span>
        </div>

        <div className="event-actor">
          <span className="actor-primary">
            {/* "by Not recorded" reads as a name; an event with no actor states
                the absence on its own instead. */}
            {unattributed ? null : "by "}
            <span className={unattributed ? "actor-unattributed" : undefined}>
              {actorPrimary}
            </span>
            {actorCompany ? "" : roleSuffix}
          </span>

          {actorPerson ? (
            <span className="actor-secondary">
              {actorPerson}
              {roleSuffix}
            </span>
          ) : null}
        </div>

        {event.remark ? (
          <p className="event-remark">
            <span className="remark-label">Remark: </span>
            {event.remark}
          </p>
        ) : null}

        {hasComparison ? (
          <ChangeList
            highlight
            title={`${cumulative.length} field${
              cumulative.length === 1 ? "" : "s"
            } differ from the last approved version`}
            subtitle={comparisonSubtitle}
            changes={cumulative}
          />
        ) : null}

        {changedFields.length > 0 && !editIsTheWholeStory ? (
          <ChangeList
            title={`${changedFields.length} field${
              changedFields.length === 1 ? "" : "s"
            } changed${hasComparison ? " in this edit" : ""}`}
            changes={changedFields}
          />
        ) : null}

        {/* A backfilled row was reconstructed from the product's current state
            by the MED-653 migration, not observed as it happened. */}
        {event.metadata?.backfilled ? (
          <span className="event-note">
            Reconstructed from existing records — predates history tracking.
          </span>
        ) : null}
      </div>
    </TimelineItem>
  );
}
